import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearCart } from "../../__redux/slices/StoreSlice";
import {
  postFormData,
  postPendingEtransfer,
  sendOrderConfirmationEmail,
  updateInventory,
} from "../../components/api/api";
import Button from "../../components/Button/Button";
import { formatCurrency } from "../../utility/UtilityFuncs";
import {
  validatePaymentCalculations,
  validateStripeAmount,
} from "../../utility/validatePayment";
import "./checkoutForm.scss";

function CheckoutForm({
  pickupLocations,
  getTotals,
  appliedCoupons,
  onOrderComplete,
}) {
  const [formData, setFormData] = useState({
    date: "",
    name: "",
    email: "",
    phone: "",
    total: "",
    pickupLocation: "",
    itemsPurchased: "",
    orderId: "",
    paidWith: "",
  });
  const [payWithCreditCard, setPayWithCreditCard] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.storeSlice.cartContents);
  const appliedCoupon = useSelector((state) => state.storeSlice.appliedCoupon);

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  useEffect(() => {
    if (!payWithCreditCard) {
      setPaymentElementReady(false);
      setPaymentError(null);
    }
  }, [payWithCreditCard]);

  useEffect(() => {
    if (payWithCreditCard && !stripe && !elements) {
      const timeout = setTimeout(() => {
        setPaymentError(true);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [payWithCreditCard, stripe, elements]);

  useEffect(() => {
    if (payWithCreditCard && stripe && elements && !paymentElementReady) {
      const timeout = setTimeout(() => {
        if (!paymentElementReady) {
          setPaymentError(true);
        }
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [payWithCreditCard, stripe, elements, paymentElementReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPaymentError(null);

    if (!stripe || !elements) {
      setPaymentError(true);
      return;
    }

    if (payWithCreditCard && !paymentElementReady) {
      setPaymentError(true);
      return;
    }

    // Validate calculations before proceeding
    const totals = getTotals();
    const validation = validatePaymentCalculations(
      totals,
      cartItems,
      appliedCoupons,
      payWithCreditCard,
    );
    if (!validation.isValid) {
      console.error("Payment validation failed:", validation.errors);
      setPaymentError(true);
      alert(
        "There was an error calculating your order total. Please refresh the page and try again.",
      );
      return;
    }

    if (validation.warnings.length > 0) {
      console.warn("Payment validation warnings:", validation.warnings);
    }

    setSubmitting(true);

    // Calculate e-transfer total (subtotal - coupons + tax, no credit card fee)
    const etransferTotal = totals.subtotal - totals.couponSavings + totals.tax;
    const finalTotal = payWithCreditCard ? totals.total : etransferTotal;

    const updatedFormData = {
      ...formData,
      date: new Date().toLocaleDateString("en-CA"),
      total: formatCurrency(finalTotal),
      itemsPurchased: cartItems
        .map(
          (item) =>
            `${item.size ? `${item.size} - ` : ""}${item.title} (${item.numInCart})`,
        )
        .join("\n"),
      orderId: Math.floor(Math.random() * 9000000000) + 1000000000, // Random 10 digit number
      paidWith: payWithCreditCard ? "Credit" : "Debit",
    };

    try {
      if (payWithCreditCard) {
        const paymentElement = elements.getElement("payment");
        if (!paymentElement) {
          throw new Error("PaymentElement not found");
        }

        const result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.origin + "/thank-you",
          },
          redirect: "if_required",
        });

        if (result.error) {
          throw new Error(result.error.message);
        }

        if (
          result.paymentIntent &&
          result.paymentIntent.status === "succeeded"
        ) {
          // Validate Stripe amount matches calculated amount
          const stripeAmount = result.paymentIntent.amount;
          const stripeValidation = validateStripeAmount(
            stripeAmount,
            totals.total,
          );

          if (!stripeValidation.isValid) {
            console.error(
              "Stripe amount validation failed:",
              stripeValidation.errors,
            );
            // Log but don't block - payment already succeeded
            // This is a safety check for monitoring
          }

          // Send confirmation email only for credit card transactions
          const itemsPurchased = cartItems.map(
            (item) =>
              `${item.size ? `${item.size} - ` : ""} ${item.title}(${item.numInCart})`,
          );

          // Ensure credit card fee is 0 for e-transfer, use correct total
          const emailCosts = {
            subtotal: formatCurrency(totals.subtotal),
            total: formatCurrency(finalTotal), // Use finalTotal which excludes credit fee for e-transfer
            couponSavings: formatCurrency(totals.couponSavings),
            tax: formatCurrency(totals.tax),
            credit: formatCurrency(payWithCreditCard ? totals.credit : 0), // 0 for e-transfer
          };

          const response = await sendOrderConfirmationEmail({
            name: formData.name,
            emailAddress: formData.email,
            costs: emailCosts,
            itemsPurchased: itemsPurchased.join("\n"),
            pickupLocation: formData.pickupLocation,
            orderId: updatedFormData.orderId,
          });
          if (response) {
            console.log("Email sent successfully");
          } else {
            throw new Error("Failed to send email");
          }
        } else {
          throw new Error("Payment not successful");
        }
      }

      if (!payWithCreditCard) {
        postPendingEtransfer(updatedFormData);
      }

      // common operations between etransfer and credit card (post form data, clear cart, update inventory, navigate to thankyou)
      await postFormData(updatedFormData);

      //* @owen comment this out to avoid updating inventory
      updateInventory(transformCartObject(cartItems), true);

      // Use the validated total - use finalTotal which was already calculated correctly
      const grandTotal = finalTotal;

      // Notify parent component that order is complete (prevents redirect)
      if (onOrderComplete) {
        onOrderComplete();
      }

      // Navigate BEFORE clearing cart to prevent Checkout.js useEffect from redirecting
      navigate(
        `/thank-you?payWithCreditCard=${payWithCreditCard}&orderId=${updatedFormData.orderId}&grandTotal=${grandTotal}`,
      );
      window.scrollTo(0, 0);

      // Clear cart after navigation with a small delay to ensure navigation completes
      setTimeout(() => {
        dispatch(clearCart());
      }, 100);

      setSubmitting(false);
    } catch (error) {
      console.error("Error during form submission:", error);
      setPaymentError(true);
      setSubmitting(false);
    }
  };

  const transformCartObject = (itemsInCart) => {
    return itemsInCart.reduce((accumulator, item) => {
      // Create a unique key that combines title and size
      const key = item.size ? `${item.title}|${item.size}` : item.title;
      accumulator[key] = item.numInCart;
      return accumulator;
    }, {});
  };

  const getTotalItemCount = () => {
    return cartItems.reduce((total, item) => total + item.numInCart, 0);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const togglePaymentMethod = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPayWithCreditCard(!payWithCreditCard);
    setPaymentError(null);
  };

  return (
    <form className="form-container" onSubmit={handleSubmit}>
      <div className="contact-details">
        <h2>What's your digits?</h2>
        <div className="form-group">
          <label htmlFor="name">Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Enter Name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone:</label>
          <input
            type="tel"
            id="phone"
            placeholder="(123) 456-7890"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            placeholder="example@email.com"
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
      </div>
      <div className="pickup-locations">
        <h2 id="pickup-location-title" className="display-font">
          Pickup Location:
        </h2>
        {pickupLocations.map((location, index) => (
          <div className="location-item" key={index}>
            <input
              type="radio"
              id={`location${index}`}
              name="pickupLocation"
              value={location.LOCATION}
              onChange={handleChange}
              required={index === 0}
            />
            <label htmlFor={`location${index}`}>
              <span className="location">{location.LOCATION}</span> <br />{" "}
              {location.DATE} - {location.TIME}
            </label>
          </div>
        ))}
      </div>
      <div className="order-summary-container">
        <div className="order-summary-title-container display-font">
          <h2>Order Summary</h2>
          <p>{getTotalItemCount()} Items</p>
        </div>
        <div className="order-financial-info">
          <div>
            <h3>Subtotal:</h3>{" "}
            <span className="subtotal">
              <h3>{formatCurrency(getTotals().subtotal)}</h3>
            </span>
          </div>
          {appliedCoupons.length > 0 &&
            appliedCoupons.map((coupon, index) => (
              <div key={index}>
                <h3>{coupon.code}:</h3>
                <span className="subtotal">
                  <h3>- {formatCurrency(coupon.dollarsSaved)}</h3>
                </span>
              </div>
            ))}
          <div>
            <h3>Tax (14%):</h3>{" "}
            <span className="subtotal">
              <h3>{formatCurrency(getTotals().tax)}</h3>
            </span>
          </div>
          <div>
            <div>
              <h3>Credit Card Fee:</h3> <p>3% charge for credit card*</p>
            </div>
            <span className="est-taxes">
              <h3>
                {payWithCreditCard
                  ? formatCurrency(getTotals().credit)
                  : formatCurrency(0)}
              </h3>
            </span>
          </div>
          <div className="total">
            <h2>Total: </h2>{" "}
            <span>
              <h2>
                {payWithCreditCard
                  ? formatCurrency(getTotals().total)
                  : formatCurrency(
                      getTotals().subtotal -
                        getTotals().couponSavings +
                        getTotals().tax,
                    )}
              </h2>
            </span>
          </div>
        </div>
      </div>
      <div className="payment-section">
        <h2>Payment Details</h2>
        <Button type="button" variant="secondary" onClick={togglePaymentMethod}>
          {payWithCreditCard ? "Pay with E-Transfer" : "Pay With Credit Card"}
        </Button>
        {payWithCreditCard && paymentError && (
          <div className="payment-error-message">
            <p>
              <strong>
                We're experiencing technical difficulties with our credit card
                payment system.
              </strong>
            </p>
            <p>Please complete your order using one of these alternatives:</p>
            <ul>
              <li>
                <strong>Pay by E-Transfer:</strong> Click "Pay with E-Transfer"
                above to complete your order
              </li>
              <li>
                <strong>Contact us:</strong> Email us at{" "}
                <a href="mailto:maplegrovepermaculture@gmail.com">
                  maplegrovepermaculture@gmail.com
                </a>{" "}
                and we'll help you complete your order
              </li>
            </ul>
            <p>
              We apologize for the inconvenience and appreciate your patience!
            </p>
          </div>
        )}
        {payWithCreditCard && !paymentError && (
          <PaymentElement
            onReady={() => {
              setPaymentElementReady(true);
              setPaymentError(null);
            }}
            onError={(error) => {
              console.error("PaymentElement error:", error);
              setPaymentError(true);
            }}
            required
          />
        )}
        {!payWithCreditCard && (
          <div className="etransfer-payment-container">
            <p>
              To pay with Interac E-Transfer:
              <br />
              <span className="bold">
                Send an E-Transfer of{" "}
                <span className="dollar-total">
                  {formatCurrency(
                    getTotals().subtotal -
                      getTotals().couponSavings +
                      getTotals().tax,
                  )}
                  $
                </span>{" "}
                to maplegrovepermaculture@gmail.com
              </span>
            </p>
            <br />
            <p>
              Once the payment is confirmed, your order will be completed and
              you will receive a confirmation email!
            </p>
            <br />
          </div>
        )}
        {!paymentError && (
          <Button
            type="submit"
            className={`submit-button button-animation ${submitting ? "submitting" : ""}`}
            disabled={submitting || (payWithCreditCard && !paymentElementReady)}
          >
            {submitting ? (
              <div className="button-loading-content">
                <div className="button-spinner"></div>
                <p>
                  {payWithCreditCard
                    ? "Processing Payment..."
                    : "Submitting Order..."}
                </p>
              </div>
            ) : payWithCreditCard ? (
              <p>Complete Order</p>
            ) : (
              <p>Pay with E-Transfer</p>
            )}
          </Button>
        )}
      </div>
    </form>
  );
}

export default CheckoutForm;
